/**
 * @file useWaiterNotifications.ts
 * @description Hook managing waiter table calls, ready-for-pickup alerts, and acoustic chimes.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../../config/supabase.config';
import { fetchPendingCalls, resolveServiceCall, playTableCallSound } from '../services/waiterCalls.service';
import type { WaiterServiceCall } from '../../../types/waiter.types';

export const useWaiterNotifications = (venueId: string | null) => {
  const [calls, setCalls] = useState<WaiterServiceCall[]>([]);
  const previousCallsCountRef = useRef<number>(0);

  const loadCalls = useCallback(async () => {
    if (!venueId) return;
    try {
      const data = await fetchPendingCalls(venueId);
      if (data.length > previousCallsCountRef.current && previousCallsCountRef.current > 0) {
        playTableCallSound();
      }
      previousCallsCountRef.current = data.length;
      setCalls(data);
    } catch (err) {
      console.error('[useWaiterNotifications] Failed to load calls:', err);
    }
  }, [venueId]);

  useEffect(() => {
    if (!venueId) return;
    loadCalls();

    const channel = supabase
      .channel(`waiter-calls-live-${venueId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'waiter_requests',
          filter: `venue_id=eq.${venueId}`,
        },
        () => {
          loadCalls();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [venueId, loadCalls]);

  const handleResolveCall = async (callId: string) => {
    setCalls(prev => prev.filter(c => c.id !== callId));
    try {
      await resolveServiceCall(callId);
    } catch (err) {
      loadCalls();
      throw err;
    }
  };

  return {
    calls,
    resolveCall: handleResolveCall,
    refreshNotifications: loadCalls,
  };
};
